import { createContext, useContext, useEffect, useState } from "react";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem("driplee_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe = () => {};

    const setupAuthListener = async () => {
      try {
        const [{ auth }, { onAuthStateChanged }, { checkIsAdmin, logSignInEvent }] = await Promise.all([
          import("./firebase"),
          import("firebase/auth"),
          import("./firestoreService"),
        ]);

        unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
          if (fbUser) {
            try {
              const email = fbUser.email || "";
              const isAdmin = await checkIsAdmin(email);

              const userData = {
                id: fbUser.uid,
                uid: fbUser.uid,
                email: email || null,
                name: fbUser.displayName || (email ? email.split("@")[0] : null),
                displayName: fbUser.displayName || (email ? email.split("@")[0] : null),
                avatar: fbUser.photoURL || null,
                photoURL: fbUser.photoURL || null,
                phone_e164: fbUser.phoneNumber || null,
                phoneNumber: fbUser.phoneNumber || null,
                role: isAdmin ? "admin" : "user",
                isAdmin: isAdmin,
                plan: isAdmin ? "pro" : "free",
                subscription_status: isAdmin ? "active" : "none"
              };

              localStorage.setItem("driplee_user", JSON.stringify(userData));
              localStorage.setItem("driplee_last_user", JSON.stringify(userData));
              setUser(userData);

              // Non-blocking sign-in event log
              logSignInEvent(userData);
            } catch (err) {
              console.error("[Driplee Auth] Error resolving user profile:", err);
              const fallback = {
                id: fbUser.uid,
                uid: fbUser.uid,
                email: fbUser.email || null,
                role: "user",
                isAdmin: false
              };
              setUser(fallback);
            } finally {
              setLoading(false);
            }
          } else {
            localStorage.removeItem("driplee_user");
            setUser(null);
            setLoading(false);
          }
        });
      } catch (err) {
        console.error("[Driplee Auth] Failed to initialize dynamic Firebase SDK:", err);
        setLoading(false);
      }
    };

    const isRootLanding = typeof window !== "undefined" && (window.location.pathname === "/" || window.location.pathname === "");
    const hasCachedUser = typeof window !== "undefined" && !!localStorage.getItem("driplee_user");

    if (!isRootLanding || hasCachedUser) {
      setupAuthListener();
    } else {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(setupAuthListener, { timeout: 2500 });
      } else {
        setTimeout(setupAuthListener, 1200);
      }
    }

    return () => unsubscribe();
  }, []);

  const refreshUser = async () => {
    try {
      const [{ auth }, { checkIsAdmin }] = await Promise.all([
        import("./firebase"),
        import("./firestoreService"),
      ]);

      if (auth.currentUser) {
        const email = auth.currentUser.email || "";
        const isAdmin = await checkIsAdmin(email);
        const updated = {
          id: auth.currentUser.uid,
          uid: auth.currentUser.uid,
          email: email || null,
          name: auth.currentUser.displayName || (email ? email.split("@")[0] : null),
          displayName: auth.currentUser.displayName || (email ? email.split("@")[0] : null),
          avatar: auth.currentUser.photoURL || null,
          photoURL: auth.currentUser.photoURL || null,
          phone_e164: auth.currentUser.phoneNumber || null,
          phoneNumber: auth.currentUser.phoneNumber || null,
          role: isAdmin ? "admin" : "user",
          isAdmin: isAdmin,
          plan: isAdmin ? "pro" : "free",
          subscription_status: isAdmin ? "active" : "none"
        };
        localStorage.setItem("driplee_user", JSON.stringify(updated));
        localStorage.setItem("driplee_last_user", JSON.stringify(updated));
        setUser(updated);
        return updated;
      }
    } catch (err) {
      console.error("[Driplee Auth] Error refreshing user:", err);
    }
    return user;
  };

  const signIn = (u) => {
    if (u) {
      localStorage.setItem("driplee_user", JSON.stringify(u));
      localStorage.setItem("driplee_last_user", JSON.stringify(u));
      setUser(u);
    }
  };

  const signOut = async () => {
    try {
      const [{ auth }, { signOut: fbSignOut }] = await Promise.all([
        import("./firebase"),
        import("firebase/auth"),
      ]);
      await fbSignOut(auth);
    } catch (err) {
      console.error("[Driplee Auth] Firebase signOut error:", err);
    }
    localStorage.removeItem("driplee_user");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, signIn, signOut, refreshUser, isAdmin: user?.isAdmin || false }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
